// Copyright (c) 2020-2021, 2003 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import { Tooltip, Grid } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { observer } from 'mobx-react';
import cx from 'classnames';
// import RightArrowIcon from '@material-ui/icons/ChevronRight';

import Button from '@shared/components/Button';
import BackButton from '@shared/components/BackButton';
import { getTestAttr } from '@shared/utils/common';

export interface StepConfig {
  number: number;
  name: string;
}

const moduleName = 'Order-create';

// prop interface for the main class.
export interface StepperProps {
  className?: string;
  steps: Array<StepConfig>;
  currentStep: number;
  firstStep: number;
  lastStep: number;
  nextStepButtonState: {                // whether a step is enable.
    [key: string]: boolean
  };
  creationLoading?: boolean;
  onStepChange: (step: number) => void;
  onCreate: () => any;
  onTerminate: () => any;
}

// Class to show the button row on the 'Add order' page:
//  "back" button - "Order details" label - "Create order" button
@observer
class Stepper extends React.Component<StepperProps> {

  // Handler for the Back button.
  // Note: Currently, there is only one step.
  private handleBackBtnClick = () => {
    const { currentStep, firstStep, onStepChange, onTerminate } = this.props;

    // Bubble up request to terminate the process.
    if (currentStep === firstStep) {
      onTerminate();

      return;
    }

    // Should never get here.
    onStepChange(currentStep - 1);
  }

  // Handler for the 'Create order' button.
  // Note: Currently, there is only one step.
  private handleNextBtnClick = () => {
    const { onStepChange, currentStep, onCreate } = this.props;

    // Buble up request to create a new order.
    if (this.isCompleted) {
      onCreate();

      return;
    }

    // Should never get here.
    onStepChange(currentStep + 1);
  }

  // return whether the last step has been reached.
  private get isCompleted() {
    const { currentStep, lastStep } = this.props;

    return currentStep === lastStep;
  }

  // return whether the next step is allowed.
  private get isNextStepEnabled() {
    const { currentStep, nextStepButtonState } = this.props;

    return nextStepButtonState[currentStep];
  }

  render() {
    const { steps, currentStep, creationLoading, lastStep, nextStepButtonState } = this.props;
    const classes = withStyles.getClasses(this.props);
    const btnDescription = this.isCompleted ? 'create-order' : 'next-step';

    return (
      <Grid
        container
        className={classes.root}
        alignItems="center"
      >
        <BackButton
          text="Back"
          classes={{ root: classes.backbtn }}
          onClick={this.handleBackBtnClick}
        />

        <div className={classes.steps}>
          {
            steps.map((step, index) => {
              //const passed = step.number < currentStep;

              return (
                <React.Fragment key={step.number}>
                  <div
                    className={cx(
                      classes.step,
                      {
                        [classes.activeStep]: step.number === currentStep,
                        [classes.disabledStep]: step.number > currentStep
                      }
                    )}
                  >
                    {/*
                    // There's only 1 step, no need to print the step number
                    <div
                      className={cx(
                        classes.status,
                        {
                          [classes.passedStatus]: passed
                        }
                      )}
                    >
                      {passed ? <SuccessIcon /> : step.number}
                    </div>
                    */}
                    <div className={classes.text}>{step.name}</div>
                  </div>
                  {index < steps.length - 1 && <div className={classes.divider} />}
                </React.Fragment>
              );
            })
          }
        </div>

        <Tooltip
          placement="top-start"
          title={
            (!this.isNextStepEnabled && this.isCompleted) ? 'Please add at least one asset and at least one deliverable for every asset.' : ''
          }
        >
          <div>
            <Button
              {...getTestAttr(moduleName, btnDescription, 'btn')}
              disabled={!this.isNextStepEnabled || creationLoading}
              loading={this.isCompleted && creationLoading}
              text={(
                <div>
                  {this.isCompleted
                    ? 'Create order'
                    : <span className={classes.label}>Next step<ChevronRightIcon /></span>
                  }
                </div>
              )}
              color={this.isCompleted ? 'secondary' : 'primary'}
              variant={this.isCompleted ? 'contained' : 'outlined'}
              onClick={this.handleNextBtnClick}
              size="small"
            />
          </div>
        </Tooltip>
      </Grid>
    );
  }
}

const StepperStyled = withStyles(
  Stepper, 
  (theme, props) => ({
    "root": {
      height: 70,
      padding: theme.spacing(0, 8),
      borderBottom: '1px solid #F0F0F0',
      position: 'absolute',
      top: 0,
      left: 0,
    },
    "steps": {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      flex: 'auto',
    },
    "step": {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      '& span': {
        fontWeight: 500,
        fontSize: '13px',
      }
    },
    "disabledStep": {
      opacity: .5,
    },
    "activeStep": {
      opacity: 1,
    },
    "status": {
      width: 24,
      height: 24,
      fontSize: 12,
      borderRadius: '50% 50%',
      marginRight: theme.spacing(2),
      backgroundColor: theme.palette.colors.primaryAccent,
      color: 'white',
      lineHeight: '24px',
      textAlign: 'center',
  
      '& svg': {
        width: 11,
        height: 11,
      }
    },
    "passedStatus": {
      backgroundColor: theme.palette.colors.green,
      fontSize: 'inherit',
    },
    "text": {
      fontSize: '1.2em',
      fontWeight: 500,
    },
    "divider": {
      width: 40,
      height: 1,
      margin: theme.spacing(0, 2),
      backgroundColor: '#D8D8D8',
    },
    "backbtn": {
      margin: 0,
    },
    "label": {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      fontWeight: 500,
    }
  })
);

export default StepperStyled;